import { useState, useEffect } from "react";
import { Habit, useHabits } from "@/context/SupabaseHabitContext";
import { format, eachDayOfInterval, addMonths, startOfDay, getDay, isFirstDayOfMonth, startOfMonth, getDaysInMonth, subMonths } from "date-fns";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";

export function useHabitGridState(habit: Habit, initialMonths: number = 6) {
  const { toggleHabitCompletion, isHabitCompletedOnDate, updateHabit } = useHabits();
  const [gridData, setGridData] = useState<(Date | null)[][]>([]);
  const [months, setMonths] = useState<{name: string, index: number}[]>([]);
  const [startDate, setStartDate] = useState(() => {
    // Start from today and go backwards (like GitHub contributions)
    const today = new Date();
    return startOfMonth(subMonths(today, initialMonths - 1));
  });
  const [editMode, setEditMode] = useState(false);
  const [editedDates, setEditedDates] = useState<string[]>([]);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  
  const isMobile = useIsMobile();
  
  // Initialize edited dates when entering edit mode
  useEffect(() => {
    if (editMode) {
      setEditedDates([...habit.completedDates]);
    }
  }, [editMode, habit.completedDates]);
  
  // Navigate through time periods
  const navigateMonths = (direction: 'forward' | 'backward') => {
    setStartDate(prevDate => {
      // Use the same amount as the grid shows
      const navigationAmount = initialMonths;
      return direction === 'forward' 
        ? addMonths(prevDate, navigationAmount)
        : addMonths(prevDate, -navigationAmount);
    });
  };
  
  // Generate the dates for the grid
  useEffect(() => {
    // Calculate end date - show up to today only
    const effectiveMonths = initialMonths;
    const today = new Date();
    // End at today, no future dates
    const endDate = today;
    
    // Generate all days in the interval
    const allDates = eachDayOfInterval({
      start: startDate,
      end: endDate,
    });
    
    // Create data structure for grid
    const monthsData: {name: string, index: number}[] = [];
    const grid: (Date | null)[][] = Array(7).fill(null).map(() => []);
    
    // Process each date and organize by day of week
    let currentMonth = "";
    let weekIndex = 0;
    
    // Fill the grid with weeks, where each column is a week
    allDates.forEach((date) => {
      const dayOfWeek = getDay(date); // 0 = Sunday, 1 = Monday, etc.
      
      // Check for start of new week
      if (dayOfWeek === 0 && grid[0].length > 0) {
        weekIndex++;
      }
      
      // Check for first day of month to add month label
      if (isFirstDayOfMonth(date)) {
        const monthName = format(date, 'MMM');
        if (monthName !== currentMonth) {
          currentMonth = monthName;
          monthsData.push({
            name: monthName,
            index: weekIndex
          });
        }
      }
      
      // Ensure all rows have this week column
      for (let i = 0; i < 7; i++) {
        // Extend the array if needed
        while (grid[i].length <= weekIndex) {
          grid[i].push(null);
        }
      }
      
      // Place the date in the grid
      grid[dayOfWeek][weekIndex] = date;
    });
    
    setGridData(grid);
    setMonths(monthsData);
  }, [startDate, initialMonths, isMobile]);
  
  // Click handler to toggle completion
  const handleToggleDay = (date: Date | null) => {
    if (!date || !editMode) return; // Only allow toggling in edit mode
    
    const dateString = date.toISOString().split('T')[0];
    
    setEditedDates(prev => {
      const isCompleted = prev.includes(dateString);
      if (isCompleted) {
        return prev.filter(d => d !== dateString);
      } else {
        return [...prev, dateString];
      }
    });
  };
  
  // Save edited dates
  const handleSaveEdits = () => {
    updateHabit(habit.id, { completedDates: editedDates });
    setEditMode(false);
    toast.success("Contributions updated successfully");
  };
  
  // Discard changes and exit edit mode
  const handleDiscardEdits = () => {
    setEditMode(false);
    setConfirmDiscard(false);
  };
  
  // Helper to determine if a date is completed
  const isDateCompleted = (date: Date | null): boolean => {
    if (!date) return false;
    
    const dateString = date.toISOString().split('T')[0];
    return editMode 
      ? editedDates.includes(dateString)
      : isHabitCompletedOnDate(habit.id, dateString);
  };
  
  return {
    gridData,
    months,
    editMode,
    confirmDiscard,
    editedDates,
    totalContributions: habit.completedDates.length,
    startDate,
    isDateCompleted,
    setEditMode,
    setConfirmDiscard,
    navigateMonths,
    handleToggleDay,
    handleSaveEdits,
    handleDiscardEdits
  };
}
